import { useEffect, useRef, useState } from 'react'

import { ENTITY_KEY_ID } from '../../../../constants/common'
import {
  useBreakpoint,
  useDebouncedState,
  useMinimizedModalsContext,
} from '../../../../hooks'
import {
  getListViewTypeWithoutPreview,
  isListViewTypeWithPreview,
} from '../../../../utils/listViewType'
import { useMultipleViewTypeListHandleClick } from '../useMultipleViewTypeListHandleClick'

import {
  CHANGE_ACTIVE_ITEM_TIMEOUT,
  PREVIEW_VIEW_TYPE_MAX_WIDTH_BREAKPOINT,
} from './useMultipleViewTypeList.constants'
import { getLayoutListViewType } from './useMultipleViewTypeList.utils'

export function useMultipleViewTypeList({
  getProfileLink,
  getProfileLinkState,
  handleReload,
  handleSetPreview,
  handleSetView,
  handleUpdateConfig,
  itemIdKey = ENTITY_KEY_ID,
  items,
  listViewType,
  minimizedModalScope,
  preview,
}) {
  const { confirmBeforeAction } = useMinimizedModalsContext()

  const [activeItem, setActiveItem] = useDebouncedState(null)
  const [isPreviewClosed, setPreviewClosed] = useState(false)

  const shouldCloseActivePreviewRef = useRef(false)

  const profileLoadersRef = useRef(null)

  function guardPreviewAction(action) {
    if (confirmBeforeAction) {
      confirmBeforeAction(action, { scope: minimizedModalScope })

      return
    }

    action()
  }

  function guardedSetActiveItem(nextValue, timeout) {
    guardPreviewAction(() => {
      setPreviewClosed(false)
      setActiveItem(nextValue, timeout)
    })
  }

  // List invalidation (page/filter/sub-tab change) force-closes an open
  // preview, so it must prompt for dirty hosts too; a close already confirmed
  // via `shouldCloseActivePreviewRef` (preview-closing item/mass action)
  // proceeds silently.
  useEffect(() => {
    if (items && !items?.find(item => item[itemIdKey] === activeItem)) {
      if (activeItem != null && !shouldCloseActivePreviewRef.current) {
        guardPreviewAction(() => setActiveItem(null))
      } else {
        setActiveItem(null)
      }
    }
  }, [items])

  const { handleClickRow, handleMouseDownRow } =
    useMultipleViewTypeListHandleClick({
      getProfileLink,
      getProfileLinkState,
      listViewType,
      setActiveItem: guardedSetActiveItem,
    })

  function handleChangeViewType(listViewType) {
    guardPreviewAction(() => {
      handleSetView(listViewType)
      handleUpdateConfig({ view: listViewType })
      setPreviewClosed(false)
      setActiveItem(null)
    })
  }

  function handleResetViewTypeToPlainBody() {
    if (isListViewTypeWithPreview(listViewType)) {
      handleChangeViewType(getListViewTypeWithoutPreview(listViewType))
    }
  }

  function handleChangeFocusedRow(value) {
    guardedSetActiveItem(value, CHANGE_ACTIVE_ITEM_TIMEOUT)
  }

  function handleCloseActivePreview() {
    guardedSetActiveItem(null)
  }

  function handleClosePreview() {
    guardPreviewAction(() => {
      setPreviewClosed(true)
      setActiveItem(null)
    })
  }

  function handleSuccessUnreadPreview() {
    handleReload()

    setActiveItem(null)
  }

  function handlePreviewResizeEnd({ width }) {
    if (preview?.size !== Math.round(width)) {
      const nextPreview = { ...preview, size: Math.round(width) }

      handleSetPreview(nextPreview)
      handleUpdateConfig({ preview: nextPreview })
    }
  }

  useBreakpoint(
    handleResetViewTypeToPlainBody,
    PREVIEW_VIEW_TYPE_MAX_WIDTH_BREAKPOINT
  )

  const layoutListViewType = getLayoutListViewType({
    isPreviewClosed,
    listViewType,
  })

  return {
    activeItem,
    guardPreviewAction,
    handleChangeFocusedRow,
    handleChangeViewType,
    handleClickRow,
    handleCloseActivePreview,
    handleClosePreview,
    handleMouseDownRow,
    handlePreviewResizeEnd,
    handleResetViewTypeToPlainBody,
    handleSuccessUnreadPreview,
    layoutListViewType,
    profileLoadersRef,
    setActiveItem,
    shouldCloseActivePreviewRef,
  }
}
